<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md"
        @click.self="$emit('close')"
      >
        <!-- Modal Card -->
        <div
          class="relative w-full max-w-2xl bg-white/95 border border-slate-200 shadow-2xl rounded-3xl overflow-hidden transition-all duration-300 transform scale-100 flex flex-col max-h-[85vh]"
          @click.stop
        >
          <!-- Header -->
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white relative">
            <div class="absolute top-0 right-0 -mt-6 -mr-6 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
            <div class="flex items-center justify-between relative z-10">
              <div class="flex items-center gap-3">
                <div class="bg-white/20 p-2.5 rounded-xl backdrop-blur-sm">
                  <font-awesome-icon icon="keyboard" class="text-xl" />
                </div>
                <div>
                  <h3 class="text-xl font-black tracking-tight">Keyboard Shortcuts</h3>
                  <p class="text-emerald-100 text-xs mt-0.5">Accelerate your workflow with hotkeys</p>
                </div>
              </div>
              <button
                @click="$emit('close')"
                class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
                title="Close (Esc)"
              >
                <font-awesome-icon icon="times" class="h-4 w-4" />
              </button>
            </div>
          </div>

          <!-- Shortcuts List -->
          <div class="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 custom-scrollbar text-slate-700">
            <!-- Categories -->
            <div v-for="category in categories" :key="category.title" class="space-y-3">
              <h4 class="text-xs font-black uppercase tracking-wider text-emerald-600 flex items-center gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                {{ category.title }}
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  v-for="shortcut in category.shortcuts"
                  :key="shortcut.description"
                  class="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-2xl hover:bg-slate-100/50 transition-colors"
                >
                  <span class="text-sm font-semibold text-slate-600">
                    {{ shortcut.description }}
                  </span>
                  <div class="flex items-center gap-1">
                    <span
                      v-for="(key, index) in shortcut.keys"
                      :key="index"
                      class="inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-white border border-slate-300 border-b-[3px] text-slate-700 rounded-md shadow-sm font-mono min-w-[20px] text-center"
                    >
                      {{ key }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="border-t border-slate-100 p-4 bg-slate-50 flex items-center justify-between text-xs text-slate-500 font-bold">
            <span class="flex items-center gap-1.5">
              <font-awesome-icon icon="info-circle" class="text-emerald-500" />
              Press <kbd class="px-1 py-0.5 bg-slate-200 rounded font-mono border-b-2 border-slate-300 text-slate-700 text-[10px]">?</kbd> or <kbd class="px-1 py-0.5 bg-slate-200 rounded font-mono border-b-2 border-slate-300 text-slate-700 text-[10px]">Alt</kbd> + <kbd class="px-1 py-0.5 bg-slate-200 rounded font-mono border-b-2 border-slate-300 text-slate-700 text-[10px]">H</kbd> again to close.
            </span>
            <span class="uppercase tracking-widest text-[9px] text-emerald-600 font-bold">Rochex Invest</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faKeyboard, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons'

library.add(faKeyboard, faTimes, faInfoCircle)

defineProps<{
  show: boolean
}>()

defineEmits(['close'])

const categories = [
  {
    title: 'Global Navigation',
    shortcuts: [
      { description: 'Toggle Help Panel', keys: ['Alt', 'H'] },
      { description: 'Toggle Sidebar Menu', keys: ['Alt', 'M'] },
      { description: 'Go to Dashboard', keys: ['Alt', 'D'] },
      { description: 'Go to Livestock Management', keys: ['Alt', 'A'] },
      { description: 'Go to Products List', keys: ['Alt', 'S'] },
      { description: 'Go to Butcher Sales', keys: ['Alt', 'B'] },
      { description: 'Go to Grocery Sales', keys: ['Alt', 'G'] },
      { description: 'Go to LocalHarvest Sales', keys: ['Alt', 'K'] },
      { description: 'Sign Out / Log Out', keys: ['Alt', 'X'] }
    ]
  },
  {
    title: 'Tabs & Modals',
    shortcuts: [
      { description: 'Open Record Sale Modal', keys: ['Alt', 'N'] },
      { description: 'Switch to Tab 1 (Register)', keys: ['Alt', '1'] },
      { description: 'Switch to Tab 2 (View Table)', keys: ['Alt', '2'] },
      { description: 'Close Modal / Cancel Form', keys: ['Esc'] }
    ]
  },
  {
    title: 'Form Shortcuts (Sale / Entry Form)',
    shortcuts: [
      { description: 'Submit / Save Form', keys: ['Ctrl', 'Enter'] },
      { description: 'Focus Product Search', keys: ['Alt', 'P'] },
      { description: 'Focus Quantity Input', keys: ['Alt', 'Q'] },
      { description: 'Focus Price per Unit', keys: ['Alt', 'U'] },
      { description: 'Focus Cash Paid Amount', keys: ['Alt', 'I'] },
      { description: 'Toggle Payment Method', keys: ['Alt', 'Y'] },
      { description: 'Focus Customer Name', keys: ['Alt', 'C'] }
    ]
  }
]
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active > div,
.fade-leave-active > div {
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fade-enter-from > div,
.fade-leave-to > div {
  transform: scale(0.95);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 20px;
}
</style>
